/**
 * OREYA — theme behaviour.
 *
 * Dependency-free. Everything is a custom element so markup stays declarative
 * and nothing runs on pages where the element is absent.
 */

(function () {
  'use strict';

  /* ----------------------------------------------------------------------
   * Helpers
   * -------------------------------------------------------------------- */

  const routes = window.OREYA_ROUTES || {};
  const strings = window.OREYA_STRINGS || {};

  function lockScroll(locked) {
    document.documentElement.classList.toggle('is-scroll-locked', locked);
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /** Replaces the inner HTML of `target` with the same node found in `doc`. */
  function swap(doc, selector, target) {
    const source = doc.querySelector(selector);
    const destination = target || document.querySelector(selector);
    if (!source || !destination) return false;
    destination.innerHTML = source.innerHTML;
    return true;
  }

  function announce(message) {
    const region = document.getElementById('oreya-live-region');
    if (region && message) region.textContent = message;
  }

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  }

  /* ----------------------------------------------------------------------
   * Drawer — a <dialog> with focus trapping handled natively by showModal()
   * -------------------------------------------------------------------- */

  class OreyaDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
        }
      });

      // Clicking the backdrop closes the drawer.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });

      this.dialog.addEventListener('close', () => {
        lockScroll(false);
        if (this.opener && document.body.contains(this.opener)) this.opener.focus();
      });
    }

    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener || null;
      this.dialog.showModal();
      lockScroll(true);
      const focusTarget = this.dialog.querySelector('[data-drawer-focus]');
      if (focusTarget) focusTarget.focus();
    }

    close() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }
  }

  customElements.define('oreya-drawer', OreyaDrawer);

  // Any element with [data-drawer-open="id"] toggles the matching drawer.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.getAttribute('data-drawer-open'));
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(trigger);
  });

  /* ----------------------------------------------------------------------
   * Quantity stepper
   * -------------------------------------------------------------------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-qty-step]');
        if (!button) return;
        event.preventDefault();
        const step = Number(button.getAttribute('data-qty-step'));
        const min = Number(this.input.min || 1);
        const max = this.input.max ? Number(this.input.max) : Infinity;
        const next = Math.min(max, Math.max(min, (Number(this.input.value) || min) + step));
        if (next === Number(this.input.value)) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  customElements.define('quantity-input', QuantityInput);

  /* ----------------------------------------------------------------------
   * Cart
   * -------------------------------------------------------------------- */

  const Cart = {
    /** Section ids that should be re-rendered after every cart mutation. */
    sectionIds() {
      const ids = [];
      document.querySelectorAll('[data-cart-section]').forEach((node) => {
        const id = node.getAttribute('data-cart-section');
        if (id && !ids.includes(id)) ids.push(id);
      });
      return ids;
    },

    applySections(sections) {
      if (!sections) return;
      Object.keys(sections).forEach((id) => {
        const target = document.querySelector('[data-cart-section="' + id + '"]');
        if (!target || typeof sections[id] !== 'string') return;
        const doc = parseHTML(sections[id]);
        const source = doc.querySelector('[data-cart-section="' + id + '"]');
        if (source) target.innerHTML = source.innerHTML;
      });
      this.syncCount();
      dispatch('oreya:cart:updated');
    },

    syncCount() {
      const source = document.querySelector('[data-cart-count-source]');
      if (!source) return;
      const count = source.getAttribute('data-cart-count-source');
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = count;
        node.hidden = Number(count) === 0;
      });
    },

    async request(url, body) {
      const payload = Object.assign(
        {
          sections: this.sectionIds().join(','),
          // Without this, Shopify renders the requested sections against the
          // home page, where the cart page's own section does not exist.
          sections_url: window.location.pathname + window.location.search,
        },
        body
      );

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw data;
      return data;
    },

    async add(items) {
      const data = await this.request((routes.cart_add_url || '/cart/add') + '.js', { items });
      this.applySections(data.sections);
      return data;
    },

    async change(payload) {
      const data = await this.request((routes.cart_change_url || '/cart/change') + '.js', payload);
      this.applySections(data.sections);
      return data;
    },
  };

  window.OreyaCart = Cart;

  /** Add-to-cart form: intercepts submit, adds via AJAX, opens the drawer. */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitButton = this.querySelector('[type="submit"]');
      this.errorTarget = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(event) {
      // Falls back to a normal form POST when the drawer is disabled.
      if (this.dataset.cartType !== 'drawer') return;
      event.preventDefault();
      if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.setLoading(true);
      this.showError('');

      const formData = new FormData(this.form);
      const item = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) };
      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && String(value).trim() !== '') properties[match[1]] = value;
      });
      if (Object.keys(properties).length) item.properties = properties;

      try {
        await Cart.add([item]);
        announce(strings.added_to_cart || '');
        const drawer = document.getElementById('CartDrawer');
        if (drawer && typeof drawer.open === 'function') drawer.open(this.submitButton);
      } catch (error) {
        this.showError((error && error.description) || strings.cart_error || '');
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('is-loading', loading);
      this.submitButton.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    showError(message) {
      if (!this.errorTarget) return;
      this.errorTarget.textContent = message;
      this.errorTarget.hidden = !message;
    }
  }

  customElements.define('product-form', ProductForm);

  /** Quantity / remove controls inside the cart drawer and cart page. */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-cart-quantity]');
        if (input) this.update(input.getAttribute('data-line'), Number(input.value), input);
      });

      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (!remove) return;
        event.preventDefault();
        this.update(remove.getAttribute('data-line'), 0, remove);
      });
    }

    async update(line, quantity, source) {
      if (!line) return;
      this.classList.add('is-busy');
      try {
        await Cart.change({ line: Number(line), quantity: quantity });
        announce(strings.cart_updated || '');
      } catch (error) {
        if (source && source.form) source.form.submit();
      } finally {
        this.classList.remove('is-busy');
      }
    }
  }

  customElements.define('cart-items', CartItems);

  Cart.syncCount();

  /* ----------------------------------------------------------------------
   * Variant picker — re-renders the server-side markup so prices stay
   * correctly formatted and localised.
   * -------------------------------------------------------------------- */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.variants = this.readVariants();
      this.addEventListener('change', this.onChange.bind(this));
    }

    readVariants() {
      const script = this.querySelector('[data-variants]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return [];
      }
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('select[data-option-index]')).map((select) => select.value).concat(
        Array.from(this.querySelectorAll('input[type="radio"]:checked')).map((input) => input.value)
      );
    }

    onChange() {
      const chosen = this.selectedOptions;
      const variant = this.variants.find((candidate) =>
        candidate.options.every((option, index) => option === chosen[index])
      );

      // Mirrors the chosen swatch value next to each option label.
      this.querySelectorAll('[data-option-selected]').forEach((node) => {
        const input = this.querySelector('input[name="' + node.dataset.optionSelected + '"]:checked');
        if (input) node.textContent = input.value;
      });

      if (!variant) {
        this.renderUnavailable();
        return;
      }

      this.updateUrl(variant);
      this.updateInputs(variant);
      this.renderSection(variant);
    }

    renderUnavailable() {
      const button = document.getElementById('AddToCart-' + this.sectionId);
      if (!button) return;
      button.setAttribute('aria-disabled', 'true');
      button.disabled = true;
      const text = button.querySelector('[data-add-to-cart-text]');
      if (text) text.textContent = strings.unavailable || '';
    }

    updateUrl(variant) {
      if (!this.productUrl || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
    }

    updateInputs(variant) {
      document.querySelectorAll('[data-variant-id-input="' + this.sectionId + '"]').forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    async renderSection(variant) {
      if (!this.productUrl || !this.sectionId) return;
      const url = this.productUrl + '?variant=' + variant.id + '&section_id=' + this.sectionId;
      try {
        const response = await fetch(url);
        const doc = parseHTML(await response.text());
        ['#Price-', '#Inventory-', '#Sku-', '#AddToCartWrap-', '#StickyPrice-'].forEach((prefix) => {
          swap(doc, prefix + this.sectionId);
        });
        const media = document.getElementById('Gallery-' + this.sectionId);
        if (media && typeof media.selectMediaById === 'function' && variant.featured_media) {
          media.selectMediaById(variant.featured_media.id);
        }
      } catch (error) {
        /* Leaves the currently rendered state untouched. */
      }
    }
  }

  customElements.define('variant-picker', VariantPicker);

  /* ----------------------------------------------------------------------
   * Product media gallery
   * -------------------------------------------------------------------- */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (event) => {
          event.preventDefault();
          this.selectMediaById(thumb.getAttribute('data-media-id'));
        });
      });

      if (this.viewport) {
        this.viewport.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      }
    }

    selectMediaById(id) {
      const slide = this.querySelector('[data-media-slide="' + id + '"]');
      if (!slide || !this.viewport) return;
      this.viewport.scrollTo({ left: slide.offsetLeft - this.viewport.offsetLeft, behavior: 'smooth' });
      this.setActive(String(id));
    }

    setActive(id) {
      this.thumbs.forEach((thumb) => {
        const active = thumb.getAttribute('data-media-id') === id;
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
      const counter = this.querySelector('[data-gallery-counter]');
      if (counter) {
        const index = this.thumbs.findIndex((thumb) => thumb.getAttribute('data-media-id') === id);
        if (index > -1) counter.textContent = index + 1 + ' / ' + this.thumbs.length;
      }
    }

    onScroll() {
      if (!this.viewport) return;
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        const slides = Array.from(this.viewport.querySelectorAll('[data-media-slide]'));
        const middle = this.viewport.scrollLeft + this.viewport.clientWidth / 2;
        const current = slides.find(
          (slide) => slide.offsetLeft <= middle && slide.offsetLeft + slide.clientWidth > middle
        );
        if (current) this.setActive(current.getAttribute('data-media-slide'));
      }, 90);
    }
  }

  customElements.define('media-gallery', MediaGallery);

  /* ----------------------------------------------------------------------
   * Slider navigation (prev / next for scroll-snap rows)
   * -------------------------------------------------------------------- */

  class SliderNav extends HTMLElement {
    connectedCallback() {
      this.track = document.getElementById(this.dataset.track);
      if (!this.track) return;
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');

      if (this.prev) this.prev.addEventListener('click', () => this.scrollBy(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollBy(1));

      this.track.addEventListener('scroll', this.updateState.bind(this), { passive: true });
      window.addEventListener('resize', this.updateState.bind(this));
      this.updateState();
    }

    scrollBy(direction) {
      const first = this.track.firstElementChild;
      const step = first ? first.clientWidth + 20 : this.track.clientWidth * 0.8;
      this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
    }

    updateState() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      const scrollable = max > 0;
      this.hidden = !scrollable;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
    }
  }

  customElements.define('slider-nav', SliderNav);

  /* ----------------------------------------------------------------------
   * Predictive search (Shopify /search/suggest section rendering)
   * -------------------------------------------------------------------- */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      if (!this.input || !this.results) return;

      this.input.addEventListener('input', () => {
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(() => this.search(this.input.value.trim()), 260);
      });
    }

    async search(term) {
      if (term.length < 2) {
        this.results.innerHTML = '';
        this.results.hidden = true;
        return;
      }

      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '6',
        section_id: 'predictive-search',
      });

      try {
        const response = await fetch((routes.predictive_search_url || '/search/suggest') + '?' + params);
        const doc = parseHTML(await response.text());
        const source = doc.querySelector('[data-predictive-results]');
        this.results.innerHTML = source ? source.innerHTML : '';
        this.results.hidden = false;
      } catch (error) {
        this.results.hidden = true;
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);

  /* ----------------------------------------------------------------------
   * Generic panel toggle (used by the mobile filter panel)
   * -------------------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-toggle-target]');
    if (!trigger) return;
    const panel = document.getElementById(trigger.getAttribute('data-toggle-target'));
    if (!panel) return;
    event.preventDefault();
    const open = !panel.classList.contains('is-open');
    panel.classList.toggle('is-open', open);
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    lockScroll(open && window.matchMedia('(max-width: 989px)').matches);
    if (open) {
      const focusTarget = panel.querySelector('[data-panel-focus]');
      if (focusTarget) focusTarget.focus();
    }
  });

  /* ----------------------------------------------------------------------
   * Facets — filtering and sorting without a full page reload
   * -------------------------------------------------------------------- */

  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.addEventListener('change', () => this.apply());
      this.addEventListener('click', (event) => {
        const clear = event.target.closest('[data-facet-clear]');
        if (!clear) return;
        event.preventDefault();
        this.navigate(clear.getAttribute('href'));
      });

      window.addEventListener('popstate', () => this.navigate(window.location.href, false));
    }

    apply() {
      // Re-queried each time: the form node is replaced on every render.
      const form = this.querySelector('form');
      if (!form) return;
      const params = new URLSearchParams(new FormData(form));
      // Empty values (e.g. an untouched price range) would add `key=` noise.
      Array.from(new Set(params.keys())).forEach((key) => {
        if (params.getAll(key).every((value) => value === '')) params.delete(key);
      });
      const query = params.toString();
      this.navigate(window.location.pathname + (query ? '?' + query : ''));
    }

    async navigate(url, push) {
      const target = new URL(url, window.location.origin);
      const results = document.getElementById('CollectionResults-' + this.sectionId);
      if (results) results.classList.add('is-busy');

      const fetchUrl = new URL(target.href);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      try {
        const response = await fetch(fetchUrl.href);
        const doc = parseHTML(await response.text());
        swap(doc, '#CollectionResults-' + this.sectionId);
        swap(doc, '#Facets-' + this.sectionId);
        if (push !== false) window.history.pushState({}, '', target.href);
        const heading = document.getElementById('CollectionResults-' + this.sectionId);
        if (heading) heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (error) {
        window.location.href = target.href;
      } finally {
        if (results) results.classList.remove('is-busy');
      }
    }
  }

  customElements.define('facet-form', FacetForm);

  /* ----------------------------------------------------------------------
   * Product recommendations — fetched lazily from Shopify's engine
   * -------------------------------------------------------------------- */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;

      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );

      this.observer.observe(this);
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url);
        const doc = parseHTML(await response.text());
        const source = doc.querySelector('product-recommendations');
        if (source && source.innerHTML.trim()) {
          this.innerHTML = source.innerHTML;
          dispatch('shopify:section:load');
        }
      } catch (error) {
        /* Silently leaves the section empty. */
      }
    }
  }

  customElements.define('product-recommendations', ProductRecommendations);

  /* ----------------------------------------------------------------------
   * Sticky add-to-cart bar (mobile)
   * -------------------------------------------------------------------- */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const anchor = document.getElementById(this.dataset.anchor);
      if (!anchor || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
          });
        },
        { rootMargin: '0px 0px -100% 0px', threshold: 0 }
      );

      this.observer.observe(anchor);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  customElements.define('sticky-atc', StickyAtc);

  /* ----------------------------------------------------------------------
   * Header: compact state on scroll
   * -------------------------------------------------------------------- */

  const header = document.querySelector('[data-sticky-header]');
  if (header) {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        header.classList.toggle('is-scrolled', window.scrollY > 12);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ----------------------------------------------------------------------
   * Reveal on scroll
   * -------------------------------------------------------------------- */

  if (document.documentElement.classList.contains('reveal-enabled') && 'IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    const observeAll = () => document.querySelectorAll('.reveal:not(.is-visible)').forEach((node) => revealObserver.observe(node));
    observeAll();
    document.addEventListener('shopify:section:load', observeAll);
  }

  /* ----------------------------------------------------------------------
   * Theme editor support
   * -------------------------------------------------------------------- */

  document.addEventListener('shopify:section:load', () => Cart.syncCount());
})();
